import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  TextField,
  Grid,
  Avatar,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Stack,
  Paper,
  Alert,
} from '@mui/material';
import { Delete, CheckCircle } from '@mui/icons-material';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  API_GET_FEEDBACK_DETAILS,
  API_SUBMIT_FEEDBACK_FORM,
} from '../../Util/URLConstants';
import { align_center, align_row } from '../../Styles/VMStyles';
import MUITypography from '../../Components/MUITypography';
import { s3_vm_box_logo } from '../../Util/VMConstants';
import { EnquiryDetailsPanel } from './FeedbackTemplate';
import { returnDate } from '../../Util/UtilHelper';

const DEFAULT_COMPANY_LOGO = s3_vm_box_logo;

const statusButtons = [
  { value: 4, label: 'Excellent' },
  { value: 3, label: 'Good' },
  { value: 2, label: 'Average' },
  { value: 1, label: 'Poor' },
];

const pickSubmittedFeedback = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.submitted_feedback && typeof payload.submitted_feedback === 'object') {
    return payload.submitted_feedback;
  }
  if (payload.data?.submitted_feedback && typeof payload.data.submitted_feedback === 'object') {
    return payload.data.submitted_feedback;
  }
  const data = payload.data;
  if (data && typeof data === 'object' && (data.feedback || data.remarks != null || data.referral)) {
    return {
      feedback: data.feedback || {},
      remarks: data.remarks || '',
      referral: Array.isArray(data.referral) ? data.referral : [],
      submitted_at: data.updated_date || data.submitted_at || null,
    };
  }
  return null;
};

const isAlreadySubmittedPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return false;
  if (payload.already_submitted === true || payload.is_completed === true) return true;
  const nested = payload.data;
  if (nested && typeof nested === 'object') {
    if (nested.already_submitted === true || nested.is_completed === true) return true;
  }
  return false;
};

const isAlreadySubmittedMessage = (payload) =>
  /already submitted/i.test(String(payload?.message || payload?.error || ''));

const normalizeReferrals = (referral) => {
  if (!Array.isArray(referral)) return [];
  return referral
    .map((row) => ({
      name: row?.name || '',
      email: row?.email || '',
      phone: row?.phone || row?.phone_no || '',
    }))
    .filter((row) => row.name || row.email || row.phone);
};

const PublicFeedbackForm = () => {
  const { token } = useParams();
  const [recipient, setRecipient] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoImageLoading, setLogoImageLoading] = useState(true);
  const [logoErrorFallback, setLogoErrorFallback] = useState(false);

  const [formData, setFormData] = useState({});
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedAt, setSubmittedAt] = useState(null);
  const [errors, setErrors] = useState({});
  const [formAlert, setFormAlert] = useState('');

  const [referOption, setReferOption] = useState('');
  const [referrals, setReferrals] = useState([{ name: '', email: '', phone: '' }]);
  const [referralErrors, setReferralErrors] = useState([{}]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertTimeout, setAlertTimeout] = useState(null);
  const [remarksCharCount, setRemarksCharCount] = useState(0);
  const MAX_REMARKS = 500;

  // Email validator
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Phone validator: strip non-digits and check minimum 7 digits
  const isValidPhone = (phone) => {
    const digitsOnly = phone.replace(/\D/g, '');
    return digitsOnly.length >= 7 && digitsOnly.length <= 15;
  };

  // URL validator
  const isValidUrl = (url) => {
    try {
      const u = new URL(String(url || '').trim());
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Build list of social links
  const rateUsLinks = useMemo(() => {
    const links = Array.isArray(recipient?.sociallinks) ? recipient.sociallinks : [];
    return links
      .filter((s) => s?.url && isValidUrl(s.url))
      .map((s) => ({
        label: s.platform === 'Other' && s.customPlatform ? s.customPlatform : s.platform,
        url: s.url,
      }));
  }, [recipient?.sociallinks]);

  // Fetch feedback details (including company_logo at top-level of response)
  useEffect(() => {
    const source = axios.CancelToken.source();
    let cancelled = false;

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_GET_FEEDBACK_DETAILS}?token=${token}`, {
          cancelToken: source.token,
        });
        if (cancelled) return;

        const payload = res?.data;

        // If data is an empty array → invalid link
        if (Array.isArray(payload?.data) && payload.data.length === 0) {
          setError('Invalid or expired feedback link.');
          return;
        }

        // If data missing/null → invalid link
        if (!payload?.data) {
          setError('Invalid or expired feedback link.');
          return;
        }

        const feedbackData = payload.data;
        const apiCompanyLogo = payload.company_logo || null;
        const enquiryDetails =
          payload.enquiry_details ||
          feedbackData.enquiry_details ||
          payload.enquiry_info ||
          feedbackData.enquiry_info ||
          null;

        setRecipient({
          ...feedbackData,
          venue: feedbackData.venue || payload.venue || payload.venue_details || null,
          enquiry_details: enquiryDetails,
          enquiry_info: enquiryDetails,
          enquiry_id:
            enquiryDetails?.enquiry_id ||
            enquiryDetails?.appointment_id ||
            feedbackData.enquiry_id ||
            null,
          name:
            enquiryDetails?.name ||
            feedbackData.recipient_name ||
            feedbackData.name,
          email: enquiryDetails?.email || feedbackData.email,
          country_code: enquiryDetails?.country_code || feedbackData.country_code,
          phone_no: enquiryDetails?.phone_no || feedbackData.phone_no,
          phone: enquiryDetails?.phone || feedbackData.phone,
          address: enquiryDetails?.address || feedbackData.address,
          event_dates: enquiryDetails?.event_dates || feedbackData.event_dates,
          appointment_date:
            enquiryDetails?.appointment_date || feedbackData.appointment_date,
        });
        setCompanyLogo(
          apiCompanyLogo?.trim() ? apiCompanyLogo : DEFAULT_COMPANY_LOGO
        );
        setLogoErrorFallback(false);
        setLogoImageLoading(true);

        const categories = Array.isArray(feedbackData.categories)
          ? feedbackData.categories.filter((cat) => cat && String(cat).trim())
          : [];
        const saved = pickSubmittedFeedback(payload);
        const alreadySubmitted = isAlreadySubmittedPayload(payload) || Boolean(saved?.feedback);

        if (alreadySubmitted) {
          applySavedSubmission(
            saved || {
              feedback: feedbackData.feedback,
              remarks: feedbackData.remarks,
              referral: feedbackData.referral,
              submitted_at: feedbackData.updated_date,
            },
            categories
          );
        } else {
          const initialInputs = {};
          categories.forEach((cat) => {
            initialInputs[String(cat).trim()] = '';
          });
          setFormData(initialInputs);
          setSubmitted(false);
          setSubmittedAt(null);
        }

        setReferralErrors((prev) => Array(Math.max(referrals.length, 1)).fill({}));
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Unable to load feedback.', err);
          setError('Unable to load feedback.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDetails();

    return () => {
      cancelled = true;
      source.cancel('Component unmounted');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const applySavedSubmission = (saved, categories = []) => {
    const feedback =
      saved?.feedback && typeof saved.feedback === 'object' ? saved.feedback : {};
    const categoryKeys = [
      ...categories.map((cat) => String(cat).trim()).filter(Boolean),
      ...Object.keys(feedback),
    ];
    const uniqueKeys = [...new Set(categoryKeys)];
    const nextForm = {};
    uniqueKeys.forEach((key) => {
      nextForm[key] = feedback[key] || '';
    });
    setFormData(nextForm);

    const note = saved?.remarks || '';
    setRemarks(note);
    setRemarksCharCount(note.length);

    const refs = normalizeReferrals(saved?.referral);
    setReferrals(refs.length ? refs : [{ name: '', email: '', phone: '' }]);
    setReferralErrors(refs.length ? refs.map(() => ({})) : [{}]);
    setReferOption(refs.length ? 'yes' : 'no');
    setSubmittedAt(saved?.submitted_at || null);
    setSubmitted(true);
  };

  const logoSrc =
    companyLogo && !logoErrorFallback ? companyLogo : DEFAULT_COMPANY_LOGO;

  // rating handler
  const handleRating = (category, value) => {
    if (submitted) return;
    setFormData((prev) => ({ ...prev, [category]: value }));
    setErrors((prev) => ({ ...prev, [category]: '' }));
    clearAlert();
  };

  // Auto-clear alert after 6 seconds
  const clearAlert = () => {
    if (alertTimeout) clearTimeout(alertTimeout);
    const timer = setTimeout(() => setFormAlert(''), 6000);
    setAlertTimeout(timer);
  };

  // Show alert with auto-clear
  const showAlert = (msg) => {
    setFormAlert(msg);
    clearAlert();
  };

  // referrals handlers
  const handleReferralChange = (index, field, value) => {
    const updated = [...referrals];
    updated[index] = { ...updated[index], [field]: value };
    setReferrals(updated);

    const updatedErr = [...referralErrors];
    if (!updatedErr[index]) updatedErr[index] = {};
    updatedErr[index][field] = '';
    setReferralErrors(updatedErr);
  };

  const addReferral = () => {
    setReferrals([...referrals, { name: '', email: '', phone: '' }]);
    setReferralErrors([...referralErrors, {}]);
  };

  const removeReferral = (index) => {
    if (referrals.length <= 1) return; // guard
    setReferrals(referrals.filter((_, i) => i !== index));
    setReferralErrors(referralErrors.filter((_, i) => i !== index));
  };

  // submit
  const handleSubmit = () => {
    if (submitted) return;

    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      if (!formData[key]) newErrors[key] = 'Please select a rating';
    });

    const newReferralErrors = [];
    if (referOption === 'yes') {
      referrals.forEach((ref, i) => {
        const errs = {};
        if (!ref.name.trim()) errs.name = 'Name is required';
        if (!ref.email.trim()) {
          errs.email = 'Email is required';
        } else if (!isValidEmail(ref.email)) {
          errs.email = 'Enter a valid email';
        }
        if (!ref.phone.trim()) {
          errs.phone = 'Phone is required';
        } else if (!isValidPhone(ref.phone)) {
          errs.phone = 'Enter valid phone (min 7 digits)';
        }
        newReferralErrors[i] = errs;
      });
    }

    if (
      Object.keys(newErrors).length > 0 ||
      newReferralErrors.some((e) => Object.keys(e).length > 0)
    ) {
      setErrors(newErrors);
      setReferralErrors(newReferralErrors);
      showAlert('Please complete all required fields.');
      return;
    }

    const payload = {
      token,
      feedback: formData,
      remarks,
      referral: referOption === 'yes' ? referrals : [],
    };

    const categories = Array.isArray(recipient?.categories) ? recipient.categories : [];

    const lockInSubmission = (resp) => {
      const saved = pickSubmittedFeedback(resp) || {
        feedback: formData,
        remarks,
        referral: referOption === 'yes' ? referrals : [],
        submitted_at: resp?.submitted_at || resp?.submitted_feedback?.submitted_at,
      };
      applySavedSubmission(saved, categories);
    };

    setIsSubmitting(true);
    axios
      .post(API_SUBMIT_FEEDBACK_FORM, payload)
      .then((res) => {
        setIsSubmitting(false);
        const resp = res?.data || {};
        const already =
          isAlreadySubmittedPayload(resp) || isAlreadySubmittedMessage(resp);
        if (already) {
          lockInSubmission(resp);
          return;
        }
        if (resp.status_code && resp.status_code !== 200) {
          showAlert(resp.message || 'Failed to submit feedback. Please try again.');
          return;
        }
        lockInSubmission(resp);
      })
      .catch((err) => {
        setIsSubmitting(false);
        const resp = err?.response?.data;
        if (
          isAlreadySubmittedPayload(resp) ||
          isAlreadySubmittedMessage(resp) ||
          err?.response?.status === 409
        ) {
          lockInSubmission(resp);
          return;
        }
        console.error('Failed to submit feedback.', err);
        showAlert(
          err?.response?.status === 400
            ? 'Invalid feedback data. Please check and try again.'
            : 'Failed to submit feedback. Please try again.'
        );
      });
  };

  if (loading)
    return (
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );

  const hasCategories = Array.isArray(recipient?.categories) && recipient.categories.length > 0;
  const displayCategories = (() => {
    const fromRecipient = Array.isArray(recipient?.categories)
      ? recipient.categories.map((cat) => String(cat).trim()).filter(Boolean)
      : [];
    const fromSaved = Object.keys(formData || {}).filter(Boolean);
    return [...new Set([...fromRecipient, ...fromSaved])];
  })();
  const showCategories = displayCategories.length > 0;
  const submittedLabel = submittedAt ? returnDate(submittedAt) : '';

  return (
    <Box
      sx={{
        mx: 'auto',
        width: '100%',
        maxWidth: 880,
        p: { xs: 2, md: 4 },
      }}
    >
      <Card elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <CardHeader
          sx={{
            pb: 1,
            alignItems: 'flex-start',
            '& .MuiCardHeader-action': { alignSelf: 'center', mt: 0 },
          }}
          title={
            <MUITypography variant="h5" fontWeight={700}>
              {submitted ? 'Thank you for your feedback' : 'Share Your Feedback'}
            </MUITypography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {submitted
                ? 'Your responses have been saved and can no longer be changed.'
                : 'We appreciate your time—your input helps us improve every event.'}
            </Typography>
          }
          action={
            <Box
              sx={{
                position: 'relative',
                width: { xs: 72, sm: 96 },
                height: { xs: 48, sm: 56 },
                borderRadius: 1.5,
                bgcolor: '#f5f5f5',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <Avatar
                src={logoSrc}
                alt="Company logo"
                variant="rounded"
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 1.5,
                  bgcolor: 'transparent',
                  opacity: logoImageLoading ? 0 : 1,
                  transition: 'opacity 0.2s ease',
                  '& img': { objectFit: 'contain' },
                }}
                imgProps={{
                  onLoad: () => setLogoImageLoading(false),
                  onError: () => {
                    setLogoErrorFallback(true);
                    setLogoImageLoading(false);
                  },
                  loading: 'lazy',
                }}
              />
              {logoImageLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.85)',
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          }
        />

        <Divider />

        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <EnquiryDetailsPanel
            selectedUsers={
              Array.isArray(recipient?.selectedUsers) && recipient.selectedUsers.length
                ? recipient.selectedUsers
                : recipient
                  ? [recipient]
                  : []
            }
            enquiryId={
              recipient?.enquiry_id ||
              recipient?.enquiry_details?.enquiry_id ||
              recipient?.enquiry_details?.appointment_id
            }
            enquiryInfo={recipient?.enquiry_info || recipient?.enquiry_details}
            venue={recipient?.venue}
          />
          <Stack
            spacing={3}
            sx={
              submitted
                ? { pointerEvents: 'none', userSelect: 'text' }
                : undefined
            }
          >
            {submitted && (
              <Alert
                icon={<CheckCircle fontSize="inherit" />}
                severity="success"
                sx={{ borderRadius: 2 }}
              >
                {submittedLabel
                  ? `Feedback already submitted on ${submittedLabel}.`
                  : 'Feedback already submitted.'}
              </Alert>
            )}
            {formAlert && (
              <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                {formAlert}
              </Typography>
            )}

              {/* Categories */}
              {showCategories ? (
                <Stack spacing={3}>
                  {displayCategories.map((category, idx) => {
                      const catKey = String(category).trim();
                      return (
                        <Box key={idx}>
                          <Typography
                            sx={{
                              mb: 1,
                              color: errors[catKey] ? 'error.main' : 'text.primary',
                              fontWeight: 600,
                              fontSize: '1.05rem',
                            }}
                          >
                            {catKey} {errors[catKey] && '*'}
                          </Typography>
                          <Grid container spacing={1} justifyContent="flex-start">
                            {statusButtons.map((btn) => {
                              const isSelected = formData[catKey] === btn.label;
                              return (
                                <Grid item key={btn.value}>
                                  <Button
                                    variant={isSelected ? 'contained' : 'outlined'}
                                    onClick={() => handleRating(catKey, btn.label)}
                                    disabled={submitted}
                                    size="small"
                                    sx={{
                                      minWidth: 110,
                                      fontWeight: 600,
                                      textTransform: 'none',
                                      backgroundColor: isSelected ? 'primary.main' : 'transparent',
                                      color: isSelected ? 'white' : 'primary.main',
                                      borderColor: 'primary.main',
                                      '&.Mui-disabled': {
                                        backgroundColor: isSelected ? 'primary.main' : 'transparent',
                                        color: isSelected ? 'white' : 'primary.main',
                                        borderColor: 'primary.main',
                                        opacity: isSelected ? 1 : 0.55,
                                      },
                                    }}
                                  >
                                    {btn.label}
                                  </Button>
                                </Grid>
                              );
                            })}
                          </Grid>
                          {errors[catKey] && (
                            <Typography variant="caption" color="error">
                              {errors[catKey]}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                </Stack>
              ) : (
                <MUITypography>Invalid Feedback Link</MUITypography>
              )}

              {/* Remarks */}
              {(!submitted || remarks) && (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Kindly share your overall experience (optional)"
                  placeholder={`Max ${MAX_REMARKS} characters`}
                  value={remarks}
                  disabled={submitted}
                  InputProps={{ readOnly: submitted }}
                  onChange={(e) => {
                    if (submitted) return;
                    const val = e.target.value.slice(0, MAX_REMARKS);
                    setRemarks(val);
                    setRemarksCharCount(val.length);
                  }}
                  helperText={submitted ? '' : `${remarksCharCount}/${MAX_REMARKS}`}
                />
              </Box>
              )}

              {!submitted && <Divider />}

              {/* Refer section — hide on submitted preview unless referrals were saved */}
              {(!submitted || referOption === 'yes') && (
              <Box>
                <Box sx={{ ...align_row, alignItems: 'center', mb: 1 }}>
                  <MUITypography variant="h5" sx={{ ...align_center, mr: 2, fontWeight: 700 }}>
                    Would you like to refer?
                  </MUITypography>
                  <RadioGroup
                    row
                    value={referOption}
                    onChange={(e) => {
                      if (submitted) return;
                      setReferOption(e.target.value);
                    }}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio disabled={submitted} />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio disabled={submitted} />}
                      label="No"
                    />
                  </RadioGroup>
                </Box>

                {referOption === 'yes' && (
                  <Stack spacing={1.5} sx={{ mt: 1 }}>
                    {referrals.map((ref, index) => (
                      <Paper
                        key={index}
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Name"
                              value={ref.name}
                              onChange={(e) =>
                                !submitted &&
                                handleReferralChange(index, 'name', e.target.value)
                              }
                              InputProps={{ readOnly: submitted }}
                              error={!!referralErrors[index]?.name}
                              helperText={referralErrors[index]?.name}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Email"
                              value={ref.email}
                              onChange={(e) =>
                                !submitted &&
                                handleReferralChange(index, 'email', e.target.value)
                              }
                              InputProps={{ readOnly: submitted }}
                              error={!!referralErrors[index]?.email}
                              helperText={referralErrors[index]?.email}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Phone"
                              value={ref.phone}
                              onChange={(e) =>
                                !submitted &&
                                handleReferralChange(index, 'phone', e.target.value)
                              }
                              InputProps={{ readOnly: submitted }}
                              error={!!referralErrors[index]?.phone}
                              helperText={referralErrors[index]?.phone}
                            />
                          </Grid>
                          <Grid item xs={12} sm={1}>
                            {!submitted && (
                              <IconButton
                                onClick={() => removeReferral(index)}
                                disabled={referrals.length === 1}
                              >
                                <Delete color="error" />
                              </IconButton>
                            )}
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}

                    {!submitted && (
                      <Box display="flex" justifyContent="flex-end">
                        <Button variant="outlined" onClick={addReferral}>
                          Add Referral
                        </Button>
                      </Box>
                    )}
                  </Stack>
                )}
              </Box>
              )}

              {/* Rate US */}
              {!submitted && rateUsLinks.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                      Rate US
                    </Typography>

                    <Stack direction="column" spacing={1.5}>
                      {rateUsLinks.map((item, idx) => (
                        <Box
                          key={`${item.url}-${idx}`}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            width: '100%',
                          }}
                        >
                          <Typography sx={{ fontWeight: 600 }}>
                            {idx + 1}. {item.label}
                          </Typography>

                          <Button
                            variant="text"
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textTransform: 'none',
                              wordBreak: 'break-all',
                              p: 0,
                              ml: 1,
                            }}
                          >
                            {item.url}
                          </Button>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </>
              )}

              {/* Submit */}
              {!submitted && (
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!hasCategories || isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                  </Button>
                </Box>
              )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PublicFeedbackForm;
